<template>
  <div class="medication-progress">
    <v-row class="v-patient-overview-medication-progress">
      <v-col cols="12" :md="showIMAB ? 6 : 12">
        <medication-adherence :adherenceLevel="1" />
      </v-col>
      <v-col cols="12" md="6" v-if="showIMAB">
        <v-row class="border ma-0 pa-2">
          <v-col cols="10">
            <h3 class="pb-3">{{$t('medication.medicationAdherenceBarriers')}}</h3>
            <v-row>
              <v-col cols="2" align-self="center">
                <div class="px-1 py-3 border c-medication-adherence-icon">
                  <v-row align-center="center" align="center">
                    <smart-icon
                      icon="mdi-clipboard-list-outline"
                      size="26"
                      :iconColor="$color.coro.gray"
                    />
                  </v-row>
                  <v-row align-center="center" align="center">
                    <span
                      class="c-medication-adherence-icon__text"
                      :style="{color: $color.coro.gray}"
                    >
                      IMAB
                    </span>
                  </v-row>
                </div>
              </v-col>
              <v-col cols="10" align-self="center">
                <div class="mx-3">
                  <medication-adherence-barriers
                    :orange="barriersOrange"
                    :red="barriersRed"
                  />
                </div>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="2" align="center">
            <p class="c-medication-adherence__reported-on" v-if="IMABDate">
              {{$ucfirst($t('pa.reportedOn'))}} {{ IMABDate }}
            </p>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row class="mb-3 v-patient-overview-medication-progress">
      <v-col cols="12" md="6">
        <div class="pa-5 border" style="height: 100%">
        <v-row>
          <v-col cols="12">
            <h3>{{ $ucfirst($t("medication.medicationAdherence")) }}</h3>
          </v-col>
          <v-col cols="4">
            <span class="v-patient-overview-medication-progress__label">
              {{ $ucfirst($t("general.from", { catalog: 'app_mobile' })) }}
            </span>
            <date-picker
              v-model="graphStartDate"
              :datePickerAttr="{
                min: this.moment(visit1Date).format('YYYY-MM-DD'),
                max: this.moment(graphEndDate).format('YYYY-MM-DD'),
              }"
              :disabledDateMessage="$ucfirst($t('validation.invalidDate.dateFromEnrolment'))"
              :hideMessageFromDate="this.moment(visit1Date).format('YYYY-MM-DD')"
              :hideMessageUntilDate="this.moment().format('YYYY-MM-DD')"
            />
          </v-col>
          <v-col cols="4">
            <span class="v-patient-overview-medication-progress__label">
              {{ $ucfirst($t("general.until", { catalog: 'app_mobile' })) }}
            </span>
            <date-picker
              v-model="graphEndDate"
              :datePickerAttr="{
                min: this.moment(graphStartDate).format('YYYY-MM-DD'),
                max: this.moment().format('YYYY-MM-DD'),
              }"
              :disabledDateMessage="$ucfirst($t('validation.invalidDate.dateFromEnrolment'))"
              :hideMessageFromDate="this.moment(visit1Date).format('YYYY-MM-DD')"
              :hideMessageUntilDate="this.moment().format('YYYY-MM-DD')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <apexchart
              height="300"
              type="line"
              :options="chartOptions"
              :series="chartSeries"
            />
          </v-col>
        </v-row>
        </div>
      </v-col>
      <v-col cols="12" md="6" class="pa-5" v-if="!showLOG"></v-col>
      <v-col cols="12" md="6" v-else>
        <div class="pa-5 border">
        <v-row>
          <v-col cols="12">
            <h3>{{$ucfirst($t('journeyProgress.levelOfGuidance'))}} 1</h3>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <vc-calendar
              :first-day-of-week="2"
              is-expanded
              :attributes="attrs"
              ref="calendar"
              @update:from-page="movedCalendarPage"
              :locale="currentLanguage"
              :from-page="initialMonthObject"
              :min-date="this.moment(visit1Date).format('YYYY-MM-DD')"
              :max-date="this.moment().format('YYYY-MM-DD')"
            />
          </v-col>
          <v-spacer />
          <v-col cols="5" class="mr-5">
            <v-row class="pb-5">
              <p>
                {{$ucfirst($t('medicationAdherenceProgress.entirePeriod'))}}:
                <strong
                  :style="{
                    color: getColorBasedOnAdherencePercentage(
                      adherenceEntirePeriod
                    ),
                  }"
                  >{{ adherenceEntirePeriod }}%</strong
                >
              </p>
              <v-progress-linear
                :color="
                  getColorBasedOnAdherencePercentage(adherenceEntirePeriod)
                "
                height="10"
                :value="adherenceEntirePeriod"
              />
            </v-row>
            <v-row class="pb-5">
              <p>
                {{$ucfirst($t('medicationAdherenceProgress.bestMonth'))}} - {{ adherenceHighestMonthName }}:
                <strong
                  :style="{
                    color: getColorBasedOnAdherencePercentage(
                      adherenceHighestMonth
                    ),
                  }"
                  >{{ adherenceHighestMonth }}%</strong
                >
              </p>
              <v-progress-linear
                :color="
                  getColorBasedOnAdherencePercentage(adherenceHighestMonth)
                "
                height="10"
                :value="adherenceHighestMonth"
              />
            </v-row>
            <v-row class="pb-10">
              <p>
                {{ $ucfirst(adherenceLast30DaysCount > 1
                  ? $t('medicationAdherenceProgress.lastNumberOfDays', {
                      params: { numberOfDays: adherenceLast30DaysCount }
                    })
                  : $t('medicationAdherenceProgress.lastDay')
                ) }}
                <strong
                  :style="{
                    color: getColorBasedOnAdherencePercentage(
                      adherenceLast30Days
                    ),
                  }"
                  >{{ adherenceLast30Days }}%</strong
                >
              </p>
              <v-progress-linear
                :color="getColorBasedOnAdherencePercentage(adherenceLast30Days)"
                height="10"
                :value="adherenceLast30Days"
              />
            </v-row>
            <v-row class="pb-3">
              <div class="inline-block">
                <div
                  class="v-patient-overview-medication-progress__legend-circle today"
                ></div>
                <span class="v-patient-overview-medication-progress__legend-label">
                  {{$ucfirst($t('general.today', { catalog: 'app_mobile' }))}}
                </span>
              </div>
            </v-row>
            <v-row class="pb-3">
              <div class="inline-block">
                <div
                  class="v-patient-overview-medication-progress__legend-circle bg-color-coro-success-500"
                ></div>
                <span class="v-patient-overview-medication-progress__legend-label">
                  {{ $ucfirst($t('screens.medicationAdherenceScreen', { catalog: 'app_mobile' })) }}
                  = 100%
                </span>
              </div>
            </v-row>
            <v-row class="pb-3">
              <div class="inline-block">
                <div
                  class="v-patient-overview-medication-progress__legend-circle bg-color-coro-warning-500"
                ></div>
                <span class="v-patient-overview-medication-progress__legend-label">
                  50% &lt;=
                  {{ $ucfirst($t('screens.medicationAdherenceScreen', { catalog: 'app_mobile' })) }}
                  &lt; 100%
                </span>
              </div>
            </v-row>
            <v-row class="pb-3">
              <div class="inline-block">
                <div
                  class="v-patient-overview-medication-progress__legend-circle bg-color-coro-danger-500"
                ></div>
                <span class="v-patient-overview-medication-progress__legend-label">
                  {{ $ucfirst($t('screens.medicationAdherenceScreen', { catalog: 'app_mobile' })) }}
                  &lt; 50%
                </span>
              </div>
            </v-row>
          </v-col>
        </v-row>
      </div>
      </v-col>
    </v-row>
    <v-tooltip
      top
      v-model="showLabelTooltip"
      :position-x="labelTooltipX"
      :position-y="labelTooltipY"
      :max-width="200"
      content-class="progress-status-journey__label-tooltip"
    >
      {{ labelTooltipText }}
    </v-tooltip>
  </div>
</template>

<script>
import MedicationAdherence from "../../../components/Parts/MedicationAdherence";
import SmartIcon from "../../../components/Parts/SmartIcon";
import DatePicker from "../../../components/Parts/DatePicker";
import MedicationAdherenceBarriers from '../../..//components/Parts/MedicationAdherenceBarriers';
import Moment from "moment";
import { extendMoment } from "moment-range";
import gql from "graphql-tag";
import { mapGetters } from 'vuex';

/**
 * Component PatientOverviewMedicationProgress.vue
 *
 * @module PatientOverviewMedicationProgress
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.6] app/src/APP_DASHBOARD/templates/views/Patient/Medication/PatientOverviewMedicationProgress.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

const moment = extendMoment(Moment);

export default {
  name: "PatientOverviewMedicationProgress",
  components: {
    MedicationAdherence,
    DatePicker,
    MedicationAdherenceBarriers,
    SmartIcon,
  },
  computed: {
    ...mapGetters({
      patientId: 'patientSummary/id',
      currentVersion: 'patientSummary/currentVersion',
      followUpType: 'followupSession/type',
      currentLanguage: "getCurrentLanguage",
    }),
    moment: () => moment,
    visit1Date() {
      const v1 = this.$store.getters["patientSummary/visit1Date"];
      return v1 ? moment(v1) : moment();
    },
  },
  data: function () {
    return {
      showLOG: false,
      showIMAB: false,
      levelOfGuidance1: [],
      adherenceFullDates: [],
      attrs: [],
      adherenceEntirePeriod: 0,
      adherenceHighestMonth: 0,
      adherenceHighestMonthName: "",
      adherenceLast30Days: 0,
      adherenceLast30DaysCount: 30,
      graphStartDate: moment().format("YYYY-MM-DD"),
      graphEndDate: moment().format("YYYY-MM-DD"),
      selectedStartDate: moment().format("YYYY-MM-DD"),
      selectedEndDate: moment().format("YYYY-MM-DD"),
      initialMonthObject: null,
      barriersOrange: [],
      barriersRed: [],
      IMABDate: null,
      IMABVersion: null,
      IMABDateFull: null,
      showLabelTooltip: false,
      labelTooltipX: null,
      labelTooltipY: null,
      labelTooltipText: '',
      chartSeries: [
        {
          name: this.$ucfirst(this.$t("medication.adherence")),
          data: [],
        },
      ],
      chartOptions: {
        chart: {
          height: 350,
          type: "line",
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
          events: {
            markerClick: (event, chartContext, config) => {
              this.showLogDetailsOnMarkerClick(config.dataPointIndex-1)
            },
            click: (event) => {
              this.showLogDetailsOnAnnotationClick(event.clientX, event.clientY)
            },
            mouseMove: (event) => {
              this.showLogDetailsOnAnnotationClick(event.clientX, event.clientY, true)
            }
          }
        },
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: "straight",
        },
        grid: {
          row: {
            colors: ["#f3f3f3", "transparent"], // takes an array which will be repeated on columns
            opacity: 0.5,
          },
        },
        yaxis: {
          title: {
            text: this.$ucfirst(this.$t("medication.adherence")),
            style: {
              fontSize: '0.75rem'
            },
          },
          min: 0,
          max: 5,
          tickAmount: 5,
          labels: {
            minWidth: 120,
            formatter: (val) => {
              return this.formatLabel(val);
            },
            style: {
              fontSize: '0.75rem'
            },
          }
        },
        xaxis: {
          title: {
            text: this.$ucfirst(this.$t("alerts.time")),
            style: {
              fontSize: '0.75rem'
            },
            offsetY: 10
          },
          labels: {
            formatter: (val) => {
              return moment(val).format('DD/MM');
            },
            style: {
              fontSize: '0.75rem'
            },
          }
        },
        tooltip: {
          style: {
            fontSize: '0.85rem'
          },
        },
        noData: {
          text: this.$t("noData"),
          align: "center",
          verticalAlign: "middle",
          offsetX: 0,
          offsetY: -32,
          style: {
            // color: this.$color.coro["primary-500"],
            fontSize: "22px",
            fontFamily: "Futura",
          },
        },
        markers: {
          size: 4,
          strokeWidth: 1,
          hover: {
            size: 6,
          }
        },
        annotations: {
          xaxis: []
        }
      },
    };
  },
  mounted: function () {
    const start = this.$store.getters['patientSummary/lastCompletedSDMEncounterDate'];

    this.graphStartDate = start.isValid()
      ? start.format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD")

    this.getLevelOfGuidancePerPeriod();
    this.getMedicationAdherenceBarriers();
    this.getMedicationAdherenceGroupedPerDay();
  },
  watch: {
    patientId: function () {
      this.getLevelOfGuidancePerPeriod();
      this.getMedicationAdherenceGroupedPerDay();

      this.getMedicationAdherenceBarriers();
      this.showLOG = false;
    },
    graphStartDate: function () {
      this.getMedicationAdherenceGroupedPerDay();
      this.getLevelOfGuidancePerPeriod();
    },
    graphEndDate: function () {
      this.getMedicationAdherenceGroupedPerDay();
      this.getLevelOfGuidancePerPeriod();
    },
    IMABVersion() {
      this.showIMABSection();
    },
  },
  methods: {
    formatLabel(val) {
      switch(val) {
        case 0:
          return this.$ucfirst(this.$t('medicationAdherenceProgress.never'));
        case 1:
          return this.$ucfirst(this.$t('medicationAdherenceProgress.rarely'));
        case 2:
          return this.$ucfirst(this.$t('medicationAdherenceProgress.sometimes'));
        case 3:
          return this.$ucfirst(this.$t('medicationAdherenceProgress.often'));
        case 4:
          return this.$ucfirst(this.$t('medicationAdherenceProgress.mostly'));
        case 5:
          return this.$ucfirst(this.$t('medicationAdherenceProgress.always'));
        default:
          return;
      }
    },

    loadAllAdherence() {
      this.getAdherenceEntirePeriod();
      this.getBestAdherenceMonth();
      this.getAdherenceLast30Days();
      this.movedCalendarPage();
    },

    getColorBasedOnAdherencePercentage(adherencePercentage) {
      // Red: adherence < 70%
      // Orange: adherence 70% - 90%
      // Green: adherence 90% - 100%

      // if only 1 day in LOG 1

      // Red: adherence < 50%
      // Orange: adherence 50% - 99.9%
      // Green: adherence 100%

      const sameDay = moment(this.selectedStartDate).isSame(this.selectedEndDate, 'day');
      const minPercentage = sameDay ? 50 : 70;
      const maxPercentage = sameDay ? 100 : 90;

      if (adherencePercentage >= maxPercentage) {
        return this.$color.coro["success-500"];
      } else if (adherencePercentage >= minPercentage) {
        return this.$color.coro["warning-500"];
      } else {
        return this.$color.coro["danger-500"];
      }
    },

    movedCalendarPage: function (page) {
      let _page = page || {};

      if (!page) {
        const month = moment(this.selectedEndDate).format('M')
        const year = moment(this.selectedEndDate).format('YYYY')
        _page.month = +month;
        _page.year = +year;
      }

      this.createCalendarCircles(_page.month, _page.year);
    },

    showLogDetailsOnMarkerClick(index) {
      // if there is no LOG 1, don't show details
      if (!this.levelOfGuidance1.length) {
        this.showLOG = false
        return;
      }

      const dataPoint = this.adherenceFullDates[index].x;
      const annotations = this.chartOptions.annotations.xaxis;
      let annotationIndex = null;
      let show = false;

      annotations.forEach((log, i) => {
        const end = moment(log.x2) || moment();
        const start = moment(log.x);

        if (dataPoint.isAfter(start) && dataPoint.isBefore(end) && log.label.text === "1") {
          this.setSelectedPeriodDates(annotations[i].x, annotations[i].x2)
          show = true
          annotationIndex = i;
        }
      })

      // find annotation node and set active styles
      if (annotationIndex) {
        const annotationNodes = document.querySelectorAll('.apexcharts-xaxis-annotations .apexcharts-annotation-rect');
        this.resetAnnotationStyles(annotationIndex, true);
        [...annotationNodes][annotationIndex].setAttribute('fill', this.$color.coro['primary-500']);
        [...annotationNodes][annotationIndex].setAttribute('stroke', this.$color.coro['primary-800']);
        [...annotationNodes][annotationIndex].setAttribute('stroke-width', 3);
        [...annotationNodes][annotationIndex].classList.add('active');
      } else {
        this.resetAnnotationStyles(-1, true);
      }

      this.showLOG = show
    },

    showLogDetailsOnAnnotationClick(clickedX, clickedY, hover) {
      // if there is no LOG 1, don't show details
      if (!this.levelOfGuidance1.length) {
        this.showLOG = false
        return;
      }

      const annotationClass = 'v-patient-overview-medication-progress__chart-annotation';
      const chartAnnotations = this.chartOptions.annotations.xaxis;

      // get annotation nodes
      const annotations = document.querySelectorAll('.apexcharts-xaxis-annotations .apexcharts-annotation-rect');
      const annotationsRect = [...annotations].map((item,index) => {
        // get client x of annotations
        const rect = item.getBoundingClientRect()
        return {
          node: item,
          start: rect.left,
          end: rect.right,
          top: rect.top,
          bottom: rect.bottom,
          width: rect.width,
          log: item?.nextSibling?.nextSibling?.innerHTML,
          index: index
        }
      })

      // check if hovering at least one of the annotations
      // therefore within the graph and annotation boundaries
      const isInGroupRange = annotationsRect.some(item => {
        return item.start < clickedX &&
          item.end > clickedX &&
          item.top < clickedY &&
          item.bottom > clickedY
      })

      annotationsRect.forEach(item => {
        const isInRange =
          item.start < clickedX &&
          item.end > clickedX &&
          item.top < clickedY &&
          item.bottom > clickedY

        // ON HOVER
        // show annotation label as tooltip above the graph
        if (isInRange && hover) {
          this.labelTooltipX = item.start + item.width / 2;
          this.labelTooltipY = item.top;
          this.labelTooltipText = `${this.$ucfirst(this.$t("journeyProgress.levelOfGuidance"))} ${item.log}`;
          this.showLabelTooltip = true;
        } else if (!isInGroupRange) {
          this.showLabelTooltip = false;
        }
        // add hover styles for LOG 1
        if (isInRange && item.log === "1" && hover) {
          const isStyleAdded =
            item.node.getAttribute('stroke') === this.$color.coro['primary-500'] ||
            item.node.classList.contains('active')

          if (!isStyleAdded) {
            item.node.setAttribute('stroke', this.$color.coro['primary-500'])
            item.node.setAttribute('stroke-width', 2)

            if (!item.node.classList.contains(annotationClass)) {
              item.node.classList.add(annotationClass)
            }
            // reset styles for rest of annotations
            this.resetAnnotationStyles(item.index)
          }
        } else if ((!isInGroupRange || (isInRange && item.log !== "1")) && hover) {
          // reset styles for all annotations when hovering out of annotations range
          this.resetAnnotationStyles(-1)
        }

        // ON CLICK
        // show details and add active style if clicked on LOG 1, hide and remove styles on LOG 0 or LOG 2
        if (isInRange && item.log === "1" && !hover) {
          this.setSelectedPeriodDates(chartAnnotations[item.index].x, chartAnnotations[item.index].x2)
          this.showLOG = true
          this.resetAnnotationStyles(item.index, true);
          item.node.setAttribute('fill', this.$color.coro['primary-500'])
          item.node.setAttribute('stroke', this.$color.coro['primary-800'])
          item.node.setAttribute('stroke-width', 3);
          item.node.classList.add('active')
        } else if (isInRange && (item.log === "2" || item.log === "0") && !hover) {
          this.showLOG = false
          this.resetAnnotationStyles(item.index, true);
        }
      })
    },

    resetAnnotationStyles(index, resetActive) {
      const annotations = document.querySelectorAll('.apexcharts-xaxis-annotations .apexcharts-annotation-rect');
      const resetAnnotations = [...annotations].filter((item, i) => i !== index);
      resetAnnotations.forEach((item, i) => {
        const log = item?.nextSibling?.nextSibling?.innerHTML;
        // set original colors if thay have been changed
        if (item.getAttribute('stroke') !== this.getAnnotationColor(+log) &&
          (!item.classList.contains('active') || resetActive)) {

          item.setAttribute('stroke', this.getAnnotationColor(+log))
          item.setAttribute('fill', this.getAnnotationColor(+log))
          item.setAttribute('stroke-width', 1)

          if (item.classList.contains('active')) {
            item.classList.remove('active')
          }
        }
      })
    },

    getAnnotationColor(log) {
      switch (log) {
        case 0:
          return this.$color.coro["lightgray2"];
        case 1:
          return this.$color.coro['primary-300'];
        case 2:
          return this.$color.coro['primary-400'];
        case 3:
          return this.$color.coro['primary-500'];
        default:
          return this.$color.coro['primary-300'];
      }
    },

    showIMABSection() {
      // Show if SDM is opened && IMAB was filled for this visit
      if (this.followUpType === 'SDM' && this.currentVersion === this.IMABVersion) {
        this.showIMAB = true;
      }
    },

    setSelectedPeriodDates(start, end) {
      // set last month of LOG1 period as initial month
      this.initialMonthObject = {
        month: +moment(end).format('M'),
        year: +moment(end).format('YYYY'),
      }
      // set start and end date of LOG1 period - if not already set
      if (!moment(this.selectedStartDate).isSame(start) ||
          !moment(this.selectedEndDate).isSame(end)
      ) {
        this.selectedStartDate = start;
        this.selectedEndDate = end;
        this.loadAllAdherence();
      }
    },

    createCalendarCircles: function (month, year) {
      // create circles for entire period ( enrolment - today )
      let first_day = moment(this.visit1Date).format('YYYY-MM-DD');
      let last_day = moment().format('YYYY-MM-DD');
      const _month =  moment().year(year).month(month-1).format('YYYY-MM');

      // if doesn't fit in the selected period - do nothing
      if (moment(_month).isBefore(first_day, 'month') ||
          moment(_month).isAfter(last_day, 'month')) {
        return;
      }

      // get data only within one month
      if (moment(_month).isAfter(first_day, 'month')) {
        first_day = moment(_month).startOf('month').format('YYYY-MM-DD')
      }
      if (moment(_month).isBefore(last_day, 'month')) {
        last_day = moment(_month).endOf('month').format('YYYY-MM-DD')
      }

      this.$app.apolloMobileClient
        .query({
          query: gql`
            query getMyMedicationRequestSummary(
              $patientId: String!
              $firstDay: String!
              $lastDay: String!
            ) {
              myMedicationRequestSummary(
                groups: [eventDay]
                functions: { function: patientId, parameters: [$patientId] },
                conditions: { gate: and, inputs: [
                  { field: event, operator: between, parameters: [$firstDay, $lastDay] }
                ]}
              ) {
                groupKey0
                adherencePercentage
              }
            }
          `,
          variables: {
            patientId: this.patientId,
            firstDay: first_day,
            lastDay: last_day,
          },
        })
        .then((result) => {

          let attrs = [
            {
              key: "green",
              highlight: {
                color: 'green',
                fillMode: "outline",
              },
              dates: [],
            },
            {
              key: "orange",
              highlight: {
                color: 'orange',
                fillMode: "outline",
              },
              dates: [],
            },
            {
              key: "red",
              highlight: {
                color: 'red',
                fillMode: "outline",
              },
              dates: [],
            },
            {
              highlight: {
                color: 'purple',
                fillMode: "outline",
              },
              dates: new Date(),
            },
          ];

          result.data.myMedicationRequestSummary.forEach((item) => {
            // Red: adherence < 50%
            // Orange: adherence 50% - 99.9%
            // Green: adherence 100%

            if (
              moment(item.groupKey0)
                .startOf("day")
                .isSameOrAfter(moment().subtract(1, "days"))
            ) {
              return;
            }

            if (item.adherencePercentage == 1) {
              attrs[0].dates.push(new Date(item.groupKey0));
            } else if (item.adherencePercentage >= 0.5) {
              attrs[1].dates.push(new Date(item.groupKey0));
            } else if (item.adherencePercentage !== null) {
              attrs[2].dates.push(new Date(item.groupKey0));
            }
          });

          this.attrs = attrs;
        });
    },

    getMedicationAdherenceGroupedPerDay: function () {
      let first_day = moment(this.graphStartDate);
      let last_day = moment(this.graphEndDate);

      this.$app.apolloMobileClient
        .query({
          query: gql`
            query myQuestionnaireForms(
              $patientId: String!
              $firstDay: String!
              $lastDay: String!
            ) {
              myQuestionnaireForms(
                functions: {function: patientId, parameters: [$patientId]}
                conditions : {gate : and, inputs : [
                  {field : completed, value : "1"},
                  {field : key, value : "MA"},
                  {field: formEntryDate, operator: between, parameters: [$firstDay, $lastDay]}
                ]},
                orders : {field : formEntryDate, direction : asc}
              ) {
                id
                completed
                entryDate
                entries{
                  givenAnswers{
                    id
                    key
                    score
                    text
                  }
                }
              }
            }
          `,
          variables: {
            patientId: this.patientId,
            firstDay: first_day.format("YYYY-MM-DD") + " 00:00:00",
            lastDay: last_day.format("YYYY-MM-DD") + " 23:59:59",
          },
        })
        .then((result) => {
          // console.log(result);
          let newData = [];
          let fullDates = []

          result.data.myQuestionnaireForms.forEach((item) => {
            let date = moment(item.entryDate).valueOf();

            fullDates.push({x: moment(item.entryDate)})
            newData.push({
              x: date,
              y: item.entries[0].givenAnswers[0].score,
            });
          });

          const annotations = this.chartOptions.annotations.xaxis
          // check if any annotation is in the selected period
          const isAnnotationInRange = annotations.some(item => {
            const startIsBeforeUntilDate = moment(item.x).isBefore(moment(last_day.endOf('day')));
            const endIsBeforeUntilDate = moment(item.x2).isBefore(moment(last_day.endOf('day')));
            const startIsAfterFromDate = moment(item.x).isAfter(moment(first_day));
            const endIsAfterFromDate = moment(item.x2).isAfter(moment(first_day));

            if (
              (startIsAfterFromDate && endIsBeforeUntilDate) ||
              (startIsBeforeUntilDate && !endIsBeforeUntilDate) ||
              (endIsAfterFromDate && !startIsAfterFromDate)
            ) {
              return true
            } else {
              return false
            }
          })

          // add empty data for full graph
          // show full graph if there is data or annotations in the selected period
          // => otherwise the 'no data' label will be displayed
          if (newData.length || isAnnotationInRange) {
            newData.unshift({
              x: moment(first_day).valueOf(),
              y: null,
            });
            newData.push({
              x: moment(last_day.format("YYYY-MM-DD") + " 23:59:59").valueOf(),
              y: null,
            });

            this.chartOptions = {...this.chartOptions, xaxis: {
              ...this.chartOptions.xaxis, tickAmount: newData.length - 2
            }}
          }

          this.adherenceFullDates = fullDates;
          this.chartSeries = [
            {
              data: newData,
            },
          ];
        })
        .catch(err => console.log(err))
        .finally(() => {
          this.matchLastLabel()
        })
    },

    matchLastLabel() {
      // if last label don't match the graphEndDate, change tickAmount
      const lastXLabel = document.querySelector('.apexcharts-xaxis-label:last-of-type tspan');
      const dataCount = this.chartSeries[0].data.length;
      const endDate = moment(this.graphEndDate).format('DD/MM');

      if (lastXLabel && lastXLabel?.innerHTML !== endDate) {
        this.chartOptions = {
          ...this.chartOptions, xaxis: {
            ...this.chartOptions.xaxis, tickAmount: this.chartOptions.xaxis.tickAmount - (dataCount < 10 ? 2 : 1)
          }
        }
      }
    },

    getAdherenceEntirePeriod() {
      let first_day = moment(this.selectedStartDate).format('YYYY-MM-DD');
      let last_day = moment(this.selectedEndDate).format('YYYY-MM-DD');

      this.$app.apolloMobileClient
        .query({
          query: gql`
            query getMyMedicationRequestSummaryEntirePeriod(
              $patientId: String!
              $firstDay: String!
              $lastDay: String!
            ) {
              myMedicationRequestSummary(
                functions: { function: patientId, parameters: [$patientId] }
                conditions: {
                  field: event
                  operator: between
                  parameters: [$firstDay, $lastDay]
                }
              ) {
                adherencePercentage
              }
            }
          `,
          variables: {
            patientId: this.patientId,
            firstDay: first_day,
            lastDay: last_day,
          },
        })
        .then((result) => {
          this.adherenceEntirePeriod = (
            parseFloat(
              result.data.myMedicationRequestSummary[0].adherencePercentage || 0
            ) * 100
          ).toFixed(2);
        });
    },

    getBestAdherenceMonth() {
      let first_day = moment(this.selectedStartDate).format('YYYY-MM-DD');
      let last_day = moment(this.selectedEndDate).format('YYYY-MM-DD');

      this.$app.apolloMobileClient
        .query({
          query: gql`
            query getMyMedicationRequestSummaryPerMonth(
              $patientId: String!
              $firstDay: String!
              $lastDay: String!
            ) {
              myMedicationRequestSummary(
                groups: [eventMonth]
                functions: { function: patientId, parameters: [$patientId] }
                conditions: {
                  field: event
                  operator: between
                  parameters: [$firstDay, $lastDay]
                }
              ) {
                groupKey0
                adherencePercentage
              }
            }
          `,
          variables: {
            patientId: this.patientId,
            firstDay: first_day,
            lastDay: last_day,
          },
        })
        .then((result) => {
          const summary = result.data.myMedicationRequestSummary;

          let highest_month = {
            groupKey0: null,
            adherencePercentage: 0,
          };

          summary.forEach((item) => {
            if (item.adherencePercentage > highest_month.adherencePercentage) {
              highest_month = item;
            }
          });

          if(highest_month.groupKey0) {
            this.adherenceHighestMonthName = this.formatBestMonth(moment(
              highest_month.groupKey0
            ))
          } else {
            this.adherenceHighestMonthName = this.formatBestMonth(moment(last_day))
          }

          this.adherenceHighestMonth = parseFloat(
            highest_month.adherencePercentage * 100
          ).toFixed(2);
        });
    },

    formatBestMonth(date) {
      if (!date.isValid()) return;
      const month = this.$ucfirst(this.$t(`date.MMMM-${date.format('MM')}`))
      return `${month} ${date.format('YYYY')}`
    },

    getAdherenceLast30Days() {
      let first_day = moment(this.selectedStartDate).format('YYYY-MM-DD');
      let last_day = moment(this.selectedEndDate).format('YYYY-MM-DD');
      const dayCount = moment(last_day).diff(first_day, 'days');

      if (dayCount >= 30) {
        first_day = moment(last_day).subtract(30, 'days').format('YYYY-MM-DD');
        this.adherenceLast30DaysCount = 30
      } else {
        this.adherenceLast30DaysCount = dayCount + 1
      }

      this.$app.apolloMobileClient
        .query({
          query: gql`
            query getMyMedicationRequestSummary(
              $patientId: String!
              $firstDay: String!
              $lastDay: String!
            ) {
              myMedicationRequestSummary(
                functions: { function: patientId, parameters: [$patientId] }
                conditions: {
                  field: event
                  operator: between
                  parameters: [$firstDay, $lastDay]
                }
              ) {
                adherencePercentage
              }
            }
          `,
          variables: {
            patientId: this.patientId,
            firstDay: first_day,
            lastDay: last_day,
          },
        })
        .then((result) => {
          this.adherenceLast30Days = parseFloat(
            result.data.myMedicationRequestSummary[0].adherencePercentage * 100
          ).toFixed(2);
        });
    },

    async getLevelOfGuidancePerPeriod() {
      const medicationComponentLevels = await this.$app.behaviourComponentManager.getPeriodsForBehaviourComponents(this.patientId, 'MEDICATIONS', [0,1,2,3], true);

      this.levelOfGuidance1 = medicationComponentLevels.MEDICATIONS.filter(i => i.level === 1)

      const xasis = medicationComponentLevels.MEDICATIONS.map((part, i) => {
        const c = this.getAnnotationColor(part.level)
        const start = moment(this.graphStartDate).format('YYYY-MM-DD 00:00:00');
        const end = moment(this.graphEndDate).format('YYYY-MM-DD 23:59:59');
        const annotationStart = part.start
        const annotationEnd = part.end !== null ? part.end : moment()

        const isFirstLOG0 = i === 0 && part.level === 0 && moment(start).isBefore(annotationStart);
        const isAnnotationVisibleStart = moment(start).isAfter(annotationStart) &&
          moment(start).isBefore(annotationEnd);
        const isAnnotationVisibleEnd = moment(end).isBefore(annotationEnd) &&
          moment(end).isAfter(isAnnotationVisibleStart || isFirstLOG0 ? start : annotationStart);

        return {
          borderColor: c,
          fillColor: c,
          borderWidth: 0,
          offsetX: 0,
          offsetY: 0,
          strokeDashArray: 0,
          opacity: 0.6,
          // if annotation start is before graph start, use graph's start date as beginning of annotation
          // in order to make label visible
          x: isAnnotationVisibleStart || isFirstLOG0 ? moment(start).valueOf() : annotationStart.valueOf(),
          // if annotation end is after graph end, use graph's end date as end of annotation
          // in order to select correct period for LOG 1 section and hide extra label
          x2: isAnnotationVisibleEnd ? moment(end).valueOf() : annotationEnd.valueOf(),
          label: {
            borderWidth: 0,
            textAnchor: "middle",
            position: "top",
            orientation: "horizontal",
            offsetY: 15,
            offsetX: 15,
            text: (part.level).toString(),
            style: {
              background: "transparent",
              fontSize: "18px",
              color: "white",
              fontWeight: 400,
            },
          }
        }
      })

      const annotations = {
        annotations: {
          position: 'back',
          xaxis: xasis
        }
      }

      this.chartOptions = {...this.chartOptions, ...annotations}
    },

    getMedicationAdherenceBarriers: function() {
      const questionnaires = ['IMAB', 'GAD-7', 'PHQ-9'];

      // Clear possible previous values.
      this.barriersGreen = [];
      this.barriersOrange = [];
      this.barriersRed = [];

      questionnaires.forEach((questionnaire) => {
        this.$app.apolloMobileClient
          .query({
            query: gql`
              query getQuestionnaireForm(
                $patientId: String!
                $questionnaire: String!
              ) {
              myQuestionnaireForms(
                functions: { function: patientId, parameters: [$patientId] }
                conditions : {gate : and, inputs : [
                  {field : key, value : $questionnaire}
                ]},
                orders : {field : version, direction : desc}
                limit : 1
              ){
                questionnaire{
                  key
                }

                id
                completed
                score
                entryDate
                version
                entries{
                  id
                  question{
                    key
                    text
                    sequence
                  }
                  givenAnswers{
                    id
                    key
                    score
                    text
                  }
                }

              }
            }
            `,
            variables: {
              patientId: this.patientId,
              questionnaire: questionnaire
            },
          })
          .then((result) => {
            // console.log('QUESTIONNAIRE:' + questionnaire, result);
            let barriers = this.$app.patientManager.calculateBarriers(questionnaire, result);

            barriers.barriersOrange.forEach(barrier => {
              this.barriersOrange.push(this.$t(barrier, {catalog: 'app_dashboard'}));
            });

            barriers.barriersRed.forEach(barrier => {
              this.barriersRed.push(this.$t(barrier, {catalog: 'app_dashboard'}));
            });

            if(barriers.IMABDate) {
              this.IMABDate = barriers.IMABDate;
              this.IMABDateFull = result.data?.myQuestionnaireForms[0]?.entryDate
              this.IMABVersion = result.data?.myQuestionnaireForms[0]?.version
            }
          });
      })
    },
  },
};
</script>
<style lang="scss">
@import "../../../../../scss/colors";

.v-patient-overview-medication-progress__label {
  font-size: 0.9em;
}

.v-patient-overview-medication-progress__legend-circle {
  display: inline-block;
  margin-right: 8px;
  width: 15px;
  height: 15px;
  border-radius: 50%;

  &.today {
    border: 2px solid;
    border-color: map-get($coroColors, "primary-500");
  }
}

.v-patient-overview-medication-progress__legend-label {
  font-size: 0.9rem;
}

.v-patient-overview-medication-progress__chart-annotation {
  pointer-events: inherit !important;
  cursor: pointer;
}
</style>
